import { useEffect, useRef } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import db from "../../utils/firebaseClient";
import ArrowUp from "../Icons/ArrowUp";
import LoaderCircle from "../Icons/LoaderCircle";

const InputTextArea = ({
  isMaximizeChatSize,
  sendMessageContainerRef,
  isRequestTypeAgent,
  chatValue,
  handleKeyPress,
  isLoading,
  hoverNoneCss,
  lastFocusableRef,
  handleChatCreate,
  colorOne,
  handleEnter,
  step,
  handleUserData,
  setChatValue,
  chatCreate,
  getUserAgentDetails,
  setLiveChatsData,
  brandColor,
  setTextareaHeight,
  setTextareaMaximizeHeight,
  textareaHeight,
  textareaMaximizeHeight,
  userAgentDetails,
  orgId,
}) => {
  const typingTimeoutRef = useRef(null);
  const textareaRef = useRef(null);
  const isAgentTyping = userAgentDetails?.typingStatus?.user;

  const changeTextAreaHeight = (textarea) => {
    textarea.style.height = "50px";

    const newTextareaHeight = Math.min(textarea.scrollHeight, 200); // Max height of 200px
    textarea.style.height = `${newTextareaHeight}px`;

    if (textarea.scrollHeight > 200) {
      textarea.style.overflowY = "scroll";
    } else {
      textarea.style.overflowY = "hidden";
    }

    const initialContainerHeight = 88;
    const initialMaxContainerHeight = 100;

    const maxGrowthInPx = 150;
    const percentageReduction = ((newTextareaHeight - 50) / maxGrowthInPx) * 32;

    const containerHeight = initialContainerHeight - percentageReduction;
    const containerMaxHeight = initialMaxContainerHeight - percentageReduction;

    setTextareaHeight(containerHeight);
    setTextareaMaximizeHeight(containerMaxHeight);
  };

  const handleTyping = (event) => {
    setChatValue(event.target.value);
    const textarea = event.target;

    changeTextAreaHeight(textarea);

    if (step === 5 && !isAgentTyping) {
      changeTypingFlag();
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      changeTextAreaHeight(textareaRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textareaHeight, textareaMaximizeHeight]);

  useEffect(() => {
    if (step > 4) {
      try {
        // setIsLoadingConversation(true);
        const messagesRef = collection(
          db,
          "orgs",
          orgId,
          "chats",
          chatCreate,
          "messages"
        );
        getUserAgentDetails(chatCreate, orgId);
        const q = query(messagesRef, orderBy("timestamp"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
          if (!snapshot.empty) {
            const messages = snapshot.docs.map((val) => ({
              id: val.id,
              ...val.data(),
            }));

            const findIndex = messages.findIndex(
              (val) => val.id === messages[0].id
            );
            messages.splice(findIndex, findIndex + 1);

            setLiveChatsData((prev) => {
              const newMessages = messages.filter(
                (newMsg) => !prev.some((prevMsg) => prevMsg.id === newMsg.id) // Filter out duplicates by comparing `id`
              );
              return [...prev, ...newMessages];
            });
          } else {
            console.log("No messages found!");
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error in getUserAgentConversation:", error);

        return () => {
          // Empty cleanup function
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const changeTypingFlag = () => {
    const chatId = chatCreate;

    if (!chatId) {
      console.log("Chat ID is missing or invalid.");
      return;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    const chatDocRef = doc(db, "orgs", orgId, "chats", chatId);

    updateDoc(chatDocRef, {
      "typingStatus.user": true,
      "typingStatus.agent": false,
    });

    // Clear previous timeouts to avoid overlapping
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      updateDoc(chatDocRef, {
        "typingStatus.user": false,
        "typingStatus.agent": false,
      });
    }, 3000);
  };

  return (
    <>
      <div
        className={`curai-outline-none  curai-sticky curai-left-[15px] curai-right-[15px]  ${
          isMaximizeChatSize ? " " : "curai-w-full"
        }`}
        tabIndex={-1}
        ref={sendMessageContainerRef}
      >
        {localStorage.getItem("isCompletedChat") === "true" ? (
          <div
            className={`curai-box-border curai-shadow-[0_0_4px_rgba(0,0,0,0.2)] focus-within:curai-ring-2 focus-within:curai-ring-black curai-transition-[outline-width] curai-duration-[150ms] curai-items-center curai-flex ${
              isLoading ? "curai-bg-[#f7f7f7]" : ""
            } ${
              isMaximizeChatSize
                ? "curai-h-full"
                : "curai-sticky curai-w-full curai-box-border"
            }`}
          >
            <h1
              className={`curai-scroll-m-0 curai-py-[14px] curai-text-center curai-outline-none curai-max-h-[200px] curai-resize-none  curai-border-none !curai-bg-white ${
                isMaximizeChatSize
                  ? "curai-w-full  curai-text-black curai-text-base chat-fonts"
                  : "curai-text-black curai-text-base curai-w-full chat-fonts"
              }`}
            >
              You can send the transcript to yourself via email or simply close
              the chat window.
            </h1>
          </div>
        ) : (
          <div
            style={{ "--tw-ring-color": colorOne || "black" }}
            className={`curai-box-border curai-rounded-[24px] curai-shadow-[0_0_4px_rgba(0,0,0,0.2)] focus-within:curai-ring-2 curai-transition-[outline-width] curai-pl-4 curai-duration-[150ms] curai-items-end curai-flex ${
              isLoading ? "curai-bg-[#f7f7f7]" : ""
            } ${
              isMaximizeChatSize
                ? "curai-h-full"
                : "curai-sticky curai-w-full curai-box-border"
            }`}
          >
            {isRequestTypeAgent === "" ? (
              <>
                <textarea
                  id="curatu-textarea"
                  aria-label="Ask any question"
                  value={chatValue}
                  onChange={(event) => handleTyping(event)}
                  className={`curai-textarea curai-overflow-y-auto curai-scroll-m-0 curai-py-[14px] curai-rounded-[5px] curai-h-[50px] curai-min-h-[50px] curai-outline-none curai-max-h-[200px] curai-resize-none  curai-border-none h-[400px] !curai-leading-[23px] ${
                    isMaximizeChatSize
                      ? "curai-w-full  curai-text-black curai-text-base chat-fonts"
                      : "curai-text-black curai-text-base curai-w-full chat-fonts"
                  } ${isLoading ? "!curai-bg-[#f7f7f7]" : "!curai-bg-white"}`}
                  placeholder="Ask any question..."
                  onKeyDown={(event) => handleKeyPress(event)}
                  disabled={isLoading}
                  ref={(el) => {
                    if (chatValue === "") {
                      if (lastFocusableRef) lastFocusableRef.current = el;
                    }
                    if (textareaRef) textareaRef.current = el;
                  }}
                />
                <button
                  id="curatu-send-message"
                  aria-label="Send a message..."
                  type="submit"
                  className={`curai-m-[10px] curai-p-0 curai-w-[40px] curai-rounded-full curai-border-none curai-flex curai-justify-center curai-items-center 
    ${isLoading ? hoverNoneCss : ""}`}
                  onClick={() => handleChatCreate()}
                  disabled={isLoading || chatValue === ""}
                  ref={chatValue === "" ? null : lastFocusableRef}
                >
                  {!isLoading ? (
                    <div
                      className="curai-flex curai-justify-center curai-items-center curai-w-[32px] curai-h-[32px] curai-rounded-full "
                      style={{
                        backgroundColor: colorOne || "black",
                        opacity: isLoading || chatValue === "" ? 0.5 : 1,
                      }}
                    >
                      <ArrowUp color={brandColor} />
                    </div>
                  ) : (
                    <div className="curai-flex curai-justify-center curai-items-center curai-animate-spin curai-h-[32px] curai-w-[32px]">
                      <LoaderCircle color={colorOne} />
                    </div>
                  )}
                </button>
              </>
            ) : (
              <>
                <textarea
                  id="curatu-live-agent-textarea"
                  aria-label={
                    step === 2
                      ? "Please enter your name"
                      : step === 3
                      ? "Please enter your email"
                      : step === 4
                      ? "Please enter your msg"
                      : ""
                  }
                  value={chatValue}
                  onChange={(event) => handleTyping(event)}
                  className={`curai-textarea curai-overflow-y-auto curai-scroll-m-0 curai-py-[14px] curai-rounded-[5px] curai-h-[50px] curai-min-h-[50px] curai-outline-none curai-max-h-[200px] curai-resize-none  curai-border-none h-[400px] !curai-leading-[23px] ${
                    isMaximizeChatSize
                      ? "curai-w-full  curai-text-black curai-text-base chat-fonts"
                      : "curai-text-black curai-text-base curai-w-full chat-fonts"
                  } ${isLoading ? "!curai-bg-[#f7f7f7]" : "!curai-bg-white"}`}
                  placeholder={
                    step === 2
                      ? "Please enter your name"
                      : step === 3
                      ? "Please enter your email"
                      : step >= 4
                      ? "Please enter your question"
                      : ""
                  }
                  onKeyDown={(event) => handleEnter(event)}
                  disabled={isLoading}
                  ref={(el) => {
                    if (chatValue === "") {
                      if (lastFocusableRef) lastFocusableRef.current = el;
                    }
                    if (textareaRef) textareaRef.current = el;
                  }}
                />
                <button
                  id="curatu-send-message"
                  aria-label="Send a message..."
                  type="submit"
                  className={`curai-m-[10px] curai-p-0 curai-w-[40px] curai-rounded-full curai-border-none curai-flex curai-justify-center curai-items-center 
    ${isLoading ? hoverNoneCss : ""}`}
                  onClick={() => handleUserData()}
                  disabled={isLoading || chatValue === ""}
                  ref={chatValue === "" ? null : lastFocusableRef}
                >
                  {!isLoading ? (
                    <div
                      className="curai-flex curai-justify-center curai-items-center curai-w-[32px] curai-h-[32px] curai-rounded-full "
                      style={{
                        backgroundColor: colorOne || "black",
                        opacity: isLoading || chatValue === "" ? 0.5 : 1,
                      }}
                    >
                      <ArrowUp color={brandColor} />
                    </div>
                  ) : (
                    <div className="curai-flex curai-justify-center curai-items-center curai-animate-spin curai-h-[32px] curai-w-[32px]">
                      <LoaderCircle color={colorOne} />
                    </div>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default InputTextArea;
